import {
  QuestDifficulty,
  QuestRoutine,
  SkillKey,
  QuestSeries,
  QuestInstance,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { create } from "zustand"

import { REPETITIONS } from "../../../constants"
import { localStorageItems } from "../../../util/storage"

type AddEditModalErrors = {
  questName?: string
  repetition?: string
}

interface AddEditQuestStore {
  resetQuestDetails: () => void

  questName: string
  description: string | null
  photoFile: File | null
  photoUrl: string | null
  questDifficulty: QuestDifficulty | null
  coinRewardsMap: { [userId: number]: number }
  category: SkillKey | undefined | null
  routine: QuestRoutine
  repetition: boolean[]
  repeatsFrequency: number | null
  assignedUsers: number[]
  mandatory: boolean
  startDate: string | undefined | null
  reminderTimeMap: { [routine: string]: string }
  isOngoing: boolean
  minutes: any
  seconds: any
  timerLength: string | undefined | null
  reqReview: boolean
  reqNotes: boolean
  reqPhoto: boolean
  maxAutoVerifyCount: number | null
  completionCriteriaNotes: string | null
  completionCriteriaPhotoFile: File | null
  completionCriteriaPhotoUrl: string | null
  saveTemplate: boolean
  templateId: number | undefined
  customTemplateId: number | undefined
  isGrouped: boolean
  duplicateQuest: QuestInstance | null
  setQuestName: (name: string) => void
  setDescription: (description: string) => void
  setQuestDifficulty: (difficulty: QuestDifficulty | null) => void
  setPhotoFile: (photo: File | null) => void
  setPhotoUrl: (photo: string | null) => void
  setCoinRewardsMap: (userId: number, reward: number) => void
  setCategory: (category: SkillKey | undefined | null) => void
  setRoutine: (routine: QuestRoutine) => void
  setRepetition: (repetition: boolean[]) => void
  setRepeatsFrequency: (frequency: number | null) => void
  setStartDate: (string: any) => void
  setReminderTimeMap: (reminderTimeMap: { [routine: string]: string }) => void
  setIsOngoing: (bool: boolean) => void
  setMinutes: (minutes: any) => void
  setSeconds: (seconds: any) => void
  setTimerLength: (length: any) => void
  toggleUser: (userId: number) => void
  setAssignedUsers: (userIds: number[]) => void
  replaceUser: (oldUserId: number, newUserId: number) => void
  setMandatory: (bool?: boolean) => void
  setReqReview: (bool: boolean) => void
  setReqNotes: (bool?: boolean) => void
  setReqPhoto: (bool?: boolean) => void
  setMaxAutoVerifyCount: (count: number | null) => void
  setCompletionCriteriaNotes: (notes: string | null) => void
  setCompletionCriteriaPhotoFile: (photo: File | null) => void
  setCompletionCriteriaPhotoUrl: (photo: string | null) => void
  setSaveTemplate: (bool?: boolean) => void
  setTemplateId: (id: number) => void
  setCustomTemplateId: (id: number) => void
  setIsGrouped: (bool: boolean) => void
  setDuplicateQuest: (quest: QuestInstance | null) => void
  // Modal view
  isOpen: boolean
  onOpen: (selectedQuest?: QuestSeries) => void
  onClose: () => void

  errors: AddEditModalErrors
  setErrors: (errors: AddEditModalErrors) => void

  selectedQuest: QuestSeries | null
  setSelectedQuest: (quest: QuestSeries | null) => void
}

// for resetting data when modal is closed
const initQuestDataState = {
  questName: "",
  description: "",
  photoFile: null,
  photoUrl: null,
  questDifficulty: null,
  coinRewardsMap: {},
  category: undefined,
  routine: QuestRoutine.ANYTIME,
  repetition: REPETITIONS.noDays,
  repeatsFrequency: null,
  assignedUsers: [],
  mandatory: true,
  minutes: null,
  seconds: null,
  startDate: dayjs().format("YYYY-MM-DD"),
  reminderTimeMap: {},
  isOngoing: true,
  timerLength: undefined,
  reqReview: true,
  reqNotes: false,
  reqPhoto: false,
  maxAutoVerifyCount: null,
  completionCriteriaNotes: null,
  completionCriteriaPhotoFile: null,
  completionCriteriaPhotoUrl: null,
  saveTemplate: false,
  templateId: undefined,
  customTemplateId: undefined,
  isGrouped: false,
  duplicateQuest: null,
}

export const useAddEditQuestStore = create<AddEditQuestStore>((set, get) => ({
  ...initQuestDataState,

  setQuestName: (name) => set({ questName: name }),
  setDescription: (description) => set({ description }),
  setPhotoFile: (photo) => set({ photoFile: photo }),
  setPhotoUrl: (photo) => set({ photoUrl: photo }),
  setQuestDifficulty: (difficulty) => set({ questDifficulty: difficulty }),
  setCoinRewardsMap: (childId, reward) =>
    set({ coinRewardsMap: { ...get().coinRewardsMap, [childId]: reward } }),
  setCategory: (category) => set({ category }),
  setRoutine: (routine) => set({ routine }),
  setRepetition: (repetition) => set({ repetition }),
  setRepeatsFrequency: (frequency) => set({ repeatsFrequency: frequency }),
  toggleUser: (userId) => {
    const currentUsers = get().assignedUsers
    const newUsers = currentUsers.includes(userId)
      ? currentUsers.filter((id) => id !== userId)
      : [...currentUsers, userId]

    set({ assignedUsers: newUsers })
  },
  setAssignedUsers: (userIds) => set({ assignedUsers: userIds }),
  replaceUser: (oldUserId, newUserId) => {
    const newUsers = get().assignedUsers.map((userId) =>
      userId === oldUserId ? newUserId : userId
    )
    set({ assignedUsers: newUsers })
  },
  setStartDate: (startDate) => set({ startDate }),
  setReminderTimeMap: (reminderTimeMap) => set({ reminderTimeMap }),
  setIsOngoing: (bool) => set({ isOngoing: bool }),
  setMinutes: (minutes) => set({ minutes }),
  setSeconds: (seconds) => set({ seconds }),
  setTimerLength: (length) => set({ timerLength: length }),
  setMandatory: (bool) => set({ mandatory: bool }),
  setReqReview: (bool) => set({ reqReview: bool }),
  setReqNotes: (bool) => set({ reqNotes: bool }),
  setReqPhoto: (bool) => set({ reqPhoto: bool }),
  setMaxAutoVerifyCount: (count) => set({ maxAutoVerifyCount: count }),
  setCompletionCriteriaNotes: (notes) =>
    set({ completionCriteriaNotes: notes }),
  setCompletionCriteriaPhotoFile: (photo) =>
    set({ completionCriteriaPhotoFile: photo }),
  setCompletionCriteriaPhotoUrl: (photo) =>
    set({ completionCriteriaPhotoUrl: photo }),
  setSaveTemplate: (bool) => set({ saveTemplate: bool }),
  setTemplateId: (id) => set({ templateId: id }),
  setCustomTemplateId: (id) => set({ customTemplateId: id }),
  resetQuestDetails: () =>
    Object.entries(initQuestDataState).forEach(([key, val]) =>
      set({ [key]: val })
    ),
  setIsGrouped: (bool) => set({ isGrouped: bool }),
  setDuplicateQuest: (quest) => set({ duplicateQuest: quest }),
  // Modal view
  isOpen: false,
  onOpen: (selectedQuest) => {
    set({
      isOpen: true,
      errors: {},
      selectedQuest,
    })

    const isEditing = !!selectedQuest
    if (isEditing) return

    const reviewPreference = localStorage.getItem(
      localStorageItems.prefersToReviewQuest
    )
    const photoPreference = localStorage.getItem(
      localStorageItems.prefersToReviewWithPicture
    )
    const notesPreference = localStorage.getItem(
      localStorageItems.prefersToReviewWithNotes
    )
    if (!reviewPreference)
      localStorage.setItem(localStorageItems.prefersToReviewQuest, "true")

    set({
      reqReview: !reviewPreference || reviewPreference === "true",
      reqPhoto: photoPreference === "true",
      reqNotes: notesPreference === "true",
    })
  },
  onClose: () => {
    set({ isOpen: false, selectedQuest: null })
    get().resetQuestDetails()
  },

  errors: {},
  setErrors: (errors: AddEditModalErrors) => set({ errors }),

  selectedQuest: null,
  setSelectedQuest: (selectedQuest) => set({ selectedQuest }),
}))
